import { useEffect, useRef } from 'react';

export const GeometricBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId;
    let nodes = [];
    const nodeCount = 70;
    const connectionDistance = 160;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initNodes();
    };

    const initNodes = () => {
      nodes = [];
      for (let i = 0; i < nodeCount; i++) {
        const isBright = i < 8;
        nodes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * (isBright ? 0.2 : 0.3),
          vy: (Math.random() - 0.5) * (isBright ? 0.2 : 0.3),
          radius: isBright ? Math.random() * 1.5 + 2 : Math.random() * 1.5 + 0.8,
          isBright,
          phase: Math.random() * Math.PI * 2,
          phaseSpeed: 0.005 + Math.random() * 0.01,
        });
      }
    };

    let time = 0;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 1;

      nodes.forEach((node, i) => {
        node.x += node.vx;
        node.y += node.vy;
        node.phase += node.phaseSpeed;

        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;

        // Draw connections
        nodes.slice(i + 1).forEach((otherNode) => {
          const dx = otherNode.x - node.x;
          const dy = otherNode.y - node.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < connectionDistance) {
            const alpha = (1 - distance / connectionDistance) * 0.13;

            // Gradient connection line (cyan → violet for bright nodes)
            const gradient = ctx.createLinearGradient(node.x, node.y, otherNode.x, otherNode.y);
            if (node.isBright || otherNode.isBright) {
              gradient.addColorStop(0, `rgba(79, 172, 254, ${alpha * 1.5})`);
              gradient.addColorStop(1, `rgba(168, 85, 247, ${alpha})`);
            } else {
              gradient.addColorStop(0, `rgba(79, 172, 254, ${alpha})`);
              gradient.addColorStop(1, `rgba(79, 172, 254, ${alpha * 0.5})`);
            }

            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(otherNode.x, otherNode.y);
            ctx.strokeStyle = gradient;
            ctx.lineWidth = node.isBright || otherNode.isBright ? 0.8 : 0.5;
            ctx.stroke();
          }
        });

        // Draw node
        if (node.isBright) {
          // Glowing bright node
          const pulse = 0.5 + 0.5 * Math.sin(node.phase);
          const outerRadius = node.radius * (2 + pulse);

          // Outer glow
          const glow = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, outerRadius * 4);
          glow.addColorStop(0, `rgba(79, 172, 254, ${0.25 * pulse})`);
          glow.addColorStop(1, 'rgba(79, 172, 254, 0)');

          ctx.beginPath();
          ctx.arc(node.x, node.y, outerRadius * 4, 0, Math.PI * 2);
          ctx.fillStyle = glow;
          ctx.fill();

          // Core
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.radius + 0.5 * pulse, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(79, 172, 254, ${0.7 + 0.3 * pulse})`;
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(79, 172, 254, 0.20)';
          ctx.fill();
        }
      });

      animationId = requestAnimationFrame(draw);
    };

    resize();
    draw();

    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="geometric-background"
      className="fixed inset-0 pointer-events-none z-0"
      style={{ opacity: 0.85 }}
    />
  );
};

export default GeometricBackground;
